import React from 'react';
import { FeaturePageLayout } from '@/components/shared/FeaturePageLayout';
import { Database } from 'lucide-react';

export default function KnowledgeBasePage() {
  return (
    <FeaturePageLayout
      icon={Database}
      title="Knowledge Base"
      badge="Core Feature"
      description="Upload your business documents, FAQs, product catalogs, and pricing sheets. Your AI agent instantly learns from this knowledge and delivers accurate, brand-consistent answers to every customer question."
      capabilities={[
        {
          title: 'Document Upload',
          description: 'Upload PDFs, Word docs, text files, and spreadsheets. The AI extracts and indexes all relevant information automatically.',
        },
        {
          title: 'Product Catalog Integration',
          description: 'Import your product catalog with names, descriptions, pricing, and availability. The agent references this in real-time.',
        },
        {
          title: 'FAQ Management',
          description: 'Create structured Q&A pairs that the AI prioritizes for common customer questions. Add, edit, or remove anytime.',
        },
        {
          title: 'Real-Time Updates',
          description: 'Update your knowledge base whenever your business changes. New info is reflected in AI responses within seconds.',
        },
        {
          title: 'Smart Retrieval',
          description: 'The AI uses semantic search to find the most relevant information — not just keyword matching, but true understanding.',
        },
        {
          title: 'Version History',
          description: 'Track changes to your knowledge base over time. Roll back if needed to ensure response consistency.',
        },
      ]}
      howItWorks={[
        {
          title: 'Upload Your Documents',
          description: 'Drag and drop your business files — PDFs, docs, spreadsheets, or plain text. Batch upload is supported.',
        },
        {
          title: 'AI Processes & Indexes',
          description: 'Our system extracts text, understands structure, and creates a searchable knowledge graph from your documents.',
        },
        {
          title: 'Agent Learns Instantly',
          description: 'Your WhatsApp AI agent now has access to all uploaded knowledge. It references this during every customer conversation.',
        },
        {
          title: 'Update Anytime',
          description: 'Add new documents, replace old ones, or edit existing entries. Changes are reflected immediately in agent responses.',
        },
      ]}
      useCases={[
        { emoji: '📋', title: 'Product Information', description: 'Upload product sheets so customers get instant, accurate answers about specifications, pricing, and availability.' },
        { emoji: '📖', title: 'Company Policies', description: 'Add return policies, warranty info, and shipping details. Your agent handles policy questions effortlessly.' },
        { emoji: '💰', title: 'Pricing & Plans', description: 'Keep your pricing documents updated. The AI shares accurate quotes and plan comparisons in conversations.' },
        { emoji: '🔧', title: 'Troubleshooting Guides', description: 'Upload support docs to let the AI resolve technical issues and guide customers through solutions.' },
        { emoji: '📍', title: 'Location & Hours', description: 'Add store locations, business hours, and contact info. Customers get quick answers without human intervention.' },
        { emoji: '📊', title: 'Service Catalogs', description: 'Import detailed service offerings with descriptions and pricing for service-based businesses.' },
      ]}
      ctaText="Build your knowledge base in minutes"
    />
  );
}
